'use client';

import { Spinner } from '@common/components';
import { useToast } from '@common/hooks';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useInView } from 'react-intersection-observer';

import {
  AuthInfiniteQueryOptions,
  AuthMutation,
  UnauthInfiniteQueryOptions,
} from '@/api';
import { ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useAuthStore } from '@/stores/AuthStore';
import { filledInfiniteQueryOptions } from '@/utils/filledInfiniteQueryOptions';

import { Comment } from './_components/Comment';
import { CommentForm } from './_components/CommentForm';

const INITIAL_PAGE = 1;
const INITIAL_SIZE = 20;

interface TabContentCommentsProps {
  donationBusinessId: number;
}

/**
 * * 지정기부사업상세 - 응원하기 탭
 */
export const TabContentComments = ({
  donationBusinessId,
}: TabContentCommentsProps) => {
  const { toast, toastError } = useToast();
  const queryClient = useQueryClient();
  const isLogin = useAuthStore((state) => state.isLogin);
  const router = useCustomRouter();

  //* 응원 목록 조회
  const { data, fetchNextPage, hasNextPage, isFetching } = useInfiniteQuery({
    ...filledInfiniteQueryOptions(
      isLogin
        ? AuthInfiniteQueryOptions[
            'get:/wello/v2/auth/home/donation-business-comment'
          ]({
            query: {
              donationBusinessId,
              page: INITIAL_PAGE,
              size: INITIAL_SIZE,
            },
          })
        : UnauthInfiniteQueryOptions[
            'get:/wello/v2/allows/home/donation-business-comment'
          ]({
            query: {
              page: INITIAL_PAGE,
              size: INITIAL_SIZE,
              donationBusinessId,
            },
          }),
    ),
  });

  const { ref } = useInView({
    onChange: (inView) => {
      if (!isFetching && inView) fetchNextPage();
    },
  });

  //* 응원 작성
  const { mutate: createComment, isPending: createPending } = useMutation({
    ...AuthMutation['post:/wello/v2/auth/home/donation-business-comment'](),
    onSuccess: () => {
      toast({
        message: '응원이 등록되었습니다.',
      });
      queryClient.invalidateQueries();
    },
    onError: (error) => {
      toastError({
        error,
      });
    },
  });

  //* 응원 수정
  const { mutate: updateComment, isPending: updatePending } = useMutation({
    ...AuthMutation['put:/wello/v2/auth/home/donation-business-comment'](),
    onSuccess: () => {
      toast({
        message: '응원이 수정되었습니다.',
      });
      queryClient.invalidateQueries();
    },
    onError: (error) => {
      toastError({
        error,
      });
    },
  });

  //* 응원 삭제
  const { mutate: deleteComment, isPending: deletePending } = useMutation({
    ...AuthMutation['delete:/wello/v2/auth/home/donation-business-comment'](),
    onSuccess: () => {
      toast({
        message: '응원이 삭제되었습니다.',
      });
      queryClient.invalidateQueries();
    },
    onError: (error) => {
      toastError({
        error,
      });
    },
  });

  //* 응원 작성 요청
  const handleCreate = (comment: string) => {
    if (!isLogin) return;

    createComment({
      body: {
        comment,
        donationBusinessId: donationBusinessId,
      },
    });
  };

  //* 응원 수정 요청
  const handleUpdate = (comment: string, commentId: number) => {
    if (!isLogin) return;

    updateComment({
      body: {
        comment,
        donationBusinessCommentId: commentId,
      },
    });
  };

  //* 응원 삭제 요청
  const handleDelete = (commentId: number) => {
    if (!isLogin) return;

    deleteComment({
      body: {
        donationBusinessCommentId: commentId,
      },
    });
  };

  const list =
    data?.pages.flatMap((page) => page?.context?.contents ?? []) ?? [];
  const totalCount = data?.pages[0]?.context?.totalCount ?? 0;
  const isEmpty = totalCount < 1;

  return (
    <section className="flex flex-col px-16 pt-16">
      <CommentForm
        onSubmit={handleCreate}
        loading={createPending}
        onChange={() => {
          //* 비로그인 상태 일 때 댓글 작성 시도시 로그인 페이지로 이동
          if (!isLogin) {
            router.replace(
              ROUTES.LOGIN.withSearchParams({
                searchParams: {
                  redirect: location.pathname + location.search,
                },
              }),
            );

            return;
          }
        }}
      />
      {isEmpty ? (
        <div className="h-[53.125rem] text-center font-400 text-Gray-550 flex-center typo-body3">
          아직 등록된 응원이 없어요.
          <br />첫 번째 응원자가 되어보세요.
        </div>
      ) : (
        <>
          <p className="w-full pb-16 pt-24 font-600 text-Gray-900 typo-body3">
            총{' '}
            <span className="text-B-500">{totalCount?.toLocaleString()}</span>
            개의 응원하기
          </p>
          <ul className="flex flex-col">
            {list.map(
              ({
                donationBusinessCommentId: donation_business_comment_id,
                profileUrl: profile_url,
                daysAgo: days_ago,
                comment,
                mineYN: mine_yn,
                nickname,
                donationAmount: donation_amount,
              }) => (
                <li key={donation_business_comment_id}>
                  <Comment
                    profileUrl={profile_url}
                    nickname={nickname}
                    daysAgo={days_ago}
                    mineYN={mine_yn}
                    donationAmount={donation_amount}
                    comment={comment}
                    commentId={donation_business_comment_id}
                    onUpdate={handleUpdate}
                    onDelete={handleDelete}
                    loading={updatePending || deletePending}
                  />
                </li>
              ),
            )}
          </ul>
          {hasNextPage ? (
            <div className="my-30 w-full flex-center" ref={ref}>
              <Spinner />
            </div>
          ) : null}
        </>
      )}
    </section>
  );
};
